/**
 * @fileoverview A card of crop rows for What to plant's to-do rows, the See them page and the search (E19): each row is
 * the crop's name, the server's pill and the card's planting line, with "+ Add" on the right, or, once added this visit,
 * a tick and "Added" over "Open it ›" on a soft good tint. Under the card, when the crop added last is one of its rows,
 * the server's line naming the bed it went to.
 *
 * The rows, their order, the pill and every word but "+ Add" are the server's (services/plants/whatToPlantBrowse). A
 * tap on the name does nothing (there is no plant yet); "+ Add" opens the Add sheet (useAddCrop), never adds by itself
 * (rule 7). The "Not yet" pill is drawn in the accent ink (design.md 2, row 5); the others as outline pills.
 *
 * E24, the search: a row may carry the server's mark ("In your garden", "From your to-do list"), drawn as a pill before
 * the others; a row of one of her plants (plantObjId) has "Open it ›" to that plant where Add would be. Rows are known
 * by the server's key, so a type's row and its crop's are added apart.
 *
 * Where: WhatToPlantPage (From your to-do list, the search), WhatToPlantBrowsePage (each kind, the search).
 * Words (en/es): whatToPlant.add.
 *
 * @module CropRows
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Pill } from '@/shared/components/ui/Pill';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { WhatToPlantCropRow, WhatToPlantSearchRow } from '@/shared/api/whatToPlant';
import type { AddCropState } from './useAddCrop';

/** What a card of rows is handed. */
interface CropRowsProps {
  /** The rows, in the server's order; the search's carry their mark and her plant (E24). */
  rows: Array<WhatToPlantCropRow | WhatToPlantSearchRow>;
  /** The add's state: which rows were added, and the sheet's opener. */
  adder: AddCropState;
  /** The server's "Added" and "Open it". */
  addedWords: { word: string; open: string };
}

/**
 * "Open it ›" to a plant: after an add, and on a row of one of her plants (E24).
 *
 * @returns The link.
 */
const OpenIt: React.FC<{ plantObjId: number; words: string }> = ({ plantObjId, words }) => (
  <Link
    to={`/plants/${plantObjId}`}
    className="flex min-h-8 items-center font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    {words}
    <ChevronRight className="h-4 w-4" aria-hidden="true" />
  </Link>
);

/**
 * One row: name, mark, pill, line, and Add, Added, or (one of her plants) Open it.
 *
 * @returns The row.
 */
const CropRow: React.FC<{ row: WhatToPlantCropRow | WhatToPlantSearchRow } & Omit<CropRowsProps, 'rows'>> = ({ row, adder, addedWords }) => {
  const { reactTranslate: t } = useTranslation();
  const done = adder.added.get(row.key);
  const mark = 'mark' in row ? row.mark : null;
  const hers = 'plantObjId' in row ? row.plantObjId : null;
  return (
    <li className={cn('flex min-h-14 items-center gap-3 px-3 py-2', done && 'bg-verdict-good-tint/45')}>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-base font-semibold text-foreground">{row.name}</span>
          {mark && <Pill size="sm" tone="outline">{mark}</Pill>}
          {row.pill && (
            <Pill size="sm" tone="outline" className={cn(row.state === 'later' && 'border-accent-ink text-accent-ink')}>{row.pill}</Pill>
          )}
        </span>
        <span className="block text-sm text-muted-foreground">{row.line}</span>
      </span>
      {hers !== null ? (
        <span className="shrink-0 text-sm"><OpenIt plantObjId={hers} words={addedWords.open} /></span>
      ) : done ? (
        <span className="flex shrink-0 flex-col items-end text-sm">
          <span className="flex items-center gap-1 font-semibold text-verdict-good">
            <Check className="h-4 w-4" aria-hidden="true" />
            {addedWords.word}
          </span>
          <OpenIt plantObjId={done.plantObjId} words={addedWords.open} />
        </span>
      ) : (
        <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => adder.ask(row)}>
          {t('whatToPlant.add')}
        </Button>
      )}
    </li>
  );
};

/**
 * The card of rows, and under it the line naming the bed when the crop added last is one of them.
 *
 * @returns The card.
 */
export const CropRows: React.FC<CropRowsProps> = ({ rows, adder, addedWords }) => {
  const last = adder.lastKey !== null && rows.some((row) => row.key === adder.lastKey)
    ? adder.added.get(adder.lastKey) ?? null
    : null;
  return (
    <>
      <ul className="divide-y divide-border overflow-hidden rounded-lg border bg-card">
        {rows.map((row) => <CropRow key={row.key} row={row} adder={adder} addedWords={addedWords} />)}
      </ul>
      {last && <p className="mt-2 text-sm text-muted-foreground" role="status">{last.line}</p>}
    </>
  );
};
